import { InPlaceEditor } from '@syncfusion/ej2-inplace-editor';
import { FormValidator } from '@syncfusion/ej2-inputs';

import { routes } from '@/shared/routes';
import { EditableClientProps, EditableRequestBody } from '@/shared/types/editable';
import { shiftToUtc } from '@/shared/utils/date';
import { isEmptyArrayOrNull, isEmptyStringOrNull } from '@/shared/utils/misc';
import { merge } from '@/shared/utils/object';
import { StatusOptions } from '@/shared/utils/status';

import { Control, ControlValue, isSwitch } from '@/front/editable/control';
import { POST } from '@/front/fetch';
import { hideOffcanvas } from '@/front/offcanvas';

const validators: Record<string, FormValidator> = {};

const editorsById: Record<string, InPlaceEditor> = {};
const controlsById: Record<string, Control> = {};

const propsById: Record<string, EditableClientProps> = {};
const fieldsById: Record<string, string> = {};

export const setEditor = (id: string, editor: InPlaceEditor) => (editorsById[id] = editor);
export const getEditor = (id: string) => editorsById[id];

export const setControl = (id: string, control: Control) => (controlsById[id] = control);
export const getControl = (id: string) => controlsById[id];

export const setProps = (id: string, props: EditableClientProps) => (propsById[id] = props);
export const getProps = (id: string) => propsById[id];

export const setField = (id: string, value: string) => (fieldsById[id] = value);
export const getField = (id: string) => fieldsById[id];

export const addValidation = (p: EditableClientProps, e: HTMLElement) => {
  if (p.validation) {
    const form = e.closest('form');

    if (form?.id) {
      let validator = validators[form.id];

      if (!validator) {
        validator = new FormValidator(form);

        validators[form.id] = validator;

        // HACK: What if no controls have validation - this won't get called?
        // TODO: Move this logic to form component?
        form.addEventListener('save', async () => {
          if (validator.validate()) {
            const data = getFormData(form);

            const res = await POST(form.action, data);

            let options: StatusOptions | undefined;

            try {
              options = await res.json();
            } catch (error) {
              // No options - ignore
              console.info(error);
            }

            if (options?.message) {
              alert(options.message);
            }

            delete form.dataset.dirty;

            if (options?.redirectUrl) {
              window.location.href = options.redirectUrl;
            } else {
              // HACK: Assumes we're in offcanvas - might not be?
              // HACK: Assume there are grids = might not be?
              // NOTE: Needed for refreshing after task copied as onHide()
              // refreshes row that was copied but we need to show the new
              // row as well
              hideOffcanvas({ refreshGrids: true });
            }
          }
        });
      }

      validator.addRules(p.field, p.validation);
    }
  }
};

export const getFormData = (form: HTMLFormElement) => {
  let data: Record<string, ControlValue> = Object.keys(controlsById)
    .filter((id) => form.contains(controlsById[id].element))
    .map((id) => {
      const control = controlsById[id];
      const props = propsById[id];

      let value: ControlValue;
      let enabled: boolean;

      if (isSwitch(control)) {
        value = maybeNegate(props, control.checked);
        enabled = !control.disabled;
      } else {
        value = control.value;
        enabled = control.enabled;
      }

      if (!enabled) {
        return {};
      }

      const { type, defaultValue } = propsById[id];

      if (typeof value === 'string') {
        value = value.trim() || null;
      }

      if ((isEmptyStringOrNull(value) || isEmptyArrayOrNull(value)) && defaultValue !== undefined) {
        value = defaultValue;
      } else if (type === 'Integer') {
        value = parseInt(value as string);

        if (isNaN(value)) {
          value = undefined;
        }
      } else if (type === 'Float') {
        value = parseFloat(value as string);

        if (isNaN(value)) {
          value = undefined;
        }
      } else if (type === 'Date') {
        if (value) {
          value = shiftToUtc(value as Date);
        }
      }

      return {
        [fieldsById[id]]: value,
      };
    })
    .reduce(merge);

  // Add any additional input values in form (excluding within editables)
  [...form.querySelectorAll('input,textarea,select')]
    .map((element) => element as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement)
    .filter(
      (input) =>
        input.name &&
        !Object.values(fieldsById).includes(input.name) &&
        (input.dataset.save ?? 'true').toLowerCase() !== 'false',
    )
    .forEach((input) => {
      const isArray = input.name.endsWith('[]');

      const name = isArray ? input.name.substring(0, input.name.length - 2) : input.name;

      let value: string | boolean | undefined = input.value;

      if (['checkbox', 'radio'].includes(input.type)) {
        const checked = (input as HTMLInputElement).checked;

        if (input.type === 'checkbox') {
          value = checked;
        } else {
          value = checked ? input.value : undefined;
        }
      } else {
        value = input.value;
      }

      if (value !== undefined) {
        data = merge(data, {
          [name]: isArray ? [...((data[name] ?? []) as string[]), value] : value,
        });
      }
    });

  return data;
};

export const updateEditor = async (id: string, value: any) => {
  const element = editorsById[id].getRootElement();

  editorsById[id].value = value;

  element.setAttribute('data-empty', isEmptyStringOrNull(value) || isEmptyArrayOrNull(value) ? 'true' : 'false');

  const { modelId, modelName, field } = propsById[id];

  const url = routes.data.editable({ model: modelName });

  const body: EditableRequestBody = {
    params: {
      name: field,
      primaryKey: modelId,
      value,
    },
  };

  await POST(url, body);
};

export const setEditableValue = async (id: string, value: any) => {
  const editor = editorsById[id];

  if (editor) {
    await updateEditor(id, value);
  }
};

export const maybeNegate = (p: EditableClientProps, value?: boolean) => !p.negate === !!value;
